import { Vector3 } from "./Vector3.js";
export class Quaternion {
    x;
    y;
    z;
    w;
    constructor(x, y, z, w) {
        this.x = x;
        this.y = y;
        this.z = z;
        this.w = w;
    }
    static Identity() {
        return new Quaternion(0, 0, 0, 1);
    }
    static Inverse(q) {
        return new Quaternion(-q.x, -q.y, -q.z, q.w);
    }
    static XRotation(radians) {
        const halfAngle = radians * 0.5;
        return new Quaternion(Math.sin(halfAngle), 0, 0, Math.cos(halfAngle));
    }
    static YRotation(radians) {
        const halfAngle = radians * 0.5;
        return new Quaternion(0, Math.sin(halfAngle), 0, Math.cos(halfAngle));
    }
    static ZRotation(radians) {
        const halfAngle = radians * 0.5;
        return new Quaternion(0, 0, Math.sin(halfAngle), Math.cos(halfAngle));
    }
    static LookRotation(forward, up) {
        //forward, up - normalized
        const vector2 = Vector3.Cross(up, forward).normalize();
        const vector3 = Vector3.Cross(forward, vector2);
        const m00 = vector2.x;
        const m01 = vector2.y;
        const m02 = vector2.z;
        const m10 = vector3.x;
        const m11 = vector3.y;
        const m12 = vector3.z;
        const m20 = forward.x;
        const m21 = forward.y;
        const m22 = forward.z;
        const num8 = m00 + m11 + m22;
        const quaternion = Quaternion.Identity();
        if (num8 > 0) {
            let num = Math.sqrt(num8 + 1);
            quaternion.w = num * 0.5;
            num = 0.5 / num;
            quaternion.x = (m12 - m21) * num;
            quaternion.y = (m20 - m02) * num;
            quaternion.z = (m01 - m10) * num;
            return quaternion;
        }
        if (m00 >= m11 && m00 >= m22) {
            const num7 = Math.sqrt(1 + m00 - m11 - m22);
            const num4 = 0.5 / num7;
            quaternion.x = 0.5 * num7;
            quaternion.y = (m01 + m10) * num4;
            quaternion.z = (m02 + m20) * num4;
            quaternion.w = (m12 - m21) * num4;
            return quaternion;
        }
        if (m11 > m22) {
            const num6 = Math.sqrt(1 + m11 - m00 - m22);
            const num3 = 0.5 / num6;
            quaternion.x = (m10 + m01) * num3;
            quaternion.y = 0.5 * num6;
            quaternion.z = (m21 + m12) * num3;
            quaternion.w = (m20 - m02) * num3;
            return quaternion;
        }
        const num5 = Math.sqrt(1 + m22 - m00 - m11);
        const num2 = 0.5 / num5;
        quaternion.x = (m20 + m02) * num2;
        quaternion.y = (m21 + m12) * num2;
        quaternion.z = 0.5 * num5;
        quaternion.w = (m01 - m10) * num2;
        return quaternion;
    }
    static Rh2Lh2(q) {
        const res = q;
        res.z = -res.z;
        res.y = -res.y;
        return res;
    }
    static AngleAxis(angle, axis) {
        const m = Vector3.Magnitude(axis);
        const res = Quaternion.Identity();
        if (m > 0) {
            const ca = Math.cos(angle / 2);
            const sa = Math.sin(angle / 2);
            res.x = (axis.x / m) * sa;
            res.y = (axis.y / m) * sa;
            res.z = (axis.z / m) * sa;
            res.w = ca;
        }
        return res;
    }
    static FromToRotation(u, v) {
        // u, v - normalized vectors
        const cross = Vector3.Cross(u, v);
        const w = 1.0 + Vector3.Dot(u, v);
        const x = cross.x, y = cross.y, z = cross.z;
        const n = Math.sqrt(x * x + y * y + z * z + w * w);
        if (n > 0) {
            return new Quaternion(x / n, y / n, z / n, w / n);
        }
        return Quaternion.Identity();
    }
    multiply(p) {
        if (p instanceof Quaternion) {
            return new Quaternion(this.w * p.x + this.x * p.w + this.y * p.z - this.z * p.y, this.w * p.y + this.y * p.w + this.z * p.x - this.x * p.z, this.w * p.z + this.z * p.w + this.x * p.y - this.y * p.x, this.w * p.w - this.x * p.x - this.y * p.y - this.z * p.z);
        }
        if (typeof p === "number") {
            return new Quaternion(this.x * p, this.y * p, this.z * p, this.w * p);
        }
        //Vector3DQuaternion++;
        // const q1 = Quaternion.Inverse(this);
        // const qNode = new Quaternion(p.x, p.y, p.z, 0);
        // const qNode2 = this.multiply(qNode).multiply(q1);
        // return new Vector3(qNode2.x, qNode2.y, qNode2.z);
        const nw = /*this.w * p.w*/ -this.x * p.x - this.y * p.y - this.z * p.z;
        const nx = this.w * p.x /*+ this.x * p.w*/ + this.y * p.z - this.z * p.y;
        const ny = this.w * p.y /*+ this.y * p.w*/ + this.z * p.x - this.x * p.z;
        const nz = this.w * p.z /*+ this.z * p.w*/ + this.x * p.y - this.y * p.x;
        var product = new Vector3(-nw * this.x + nx * this.w - ny * this.z + nz * this.y, -nw * this.y + ny * this.w - nz * this.x + nx * this.z, -nw * this.z + nz * this.w - nx * this.y + ny * this.x);
        return product;
    }
}
